<template>
    <div v-if="loaded" class="nevs-content">
        <div class="nevs-content-top-buttons">
            <NevsButton class="success" @click="saveClick()"><i class="fa-solid fa-floppy-disk"></i>
                {{ $LANG.Get('buttons.save') }}
            </NevsButton>&nbsp;
        </div>
        <NevsCard class="form">
            <h2>{{ $LANG.Get('labels.membershipExpiryReminders') }}</h2>
            <NevsCheckbox v-model="client.send_membership_expiry_reminders"
                          :label="$LANG.Get('fields.sendMembershipExpiryReminders')"></NevsCheckbox>
            <p class="disclaimer">{{ $LANG.Get('labels.membershipExpiryReminderNote') }}</p>
            <template v-if="client.send_membership_expiry_reminders">
                <NevsNumberField v-model="client.membership_expiry_reminder_days" :decimal-places="0"
                                 :label="$LANG.Get('fields.membershipExpiryReminderDays') + ':'"></NevsNumberField>
                <NevsCheckbox v-model="client.membership_expiry_reminder_send_offer"
                              :label="$LANG.Get('fields.membershipExpiryReminderSendOffer')"></NevsCheckbox>
                <NevsCheckbox v-if="client.stripe_enabled"
                              v-model="client.membership_expiry_reminder_send_stripe_link"
                              :label="$LANG.Get('fields.membershipExpiryReminderSendStripeLink')"></NevsCheckbox>
            </template>
        </NevsCard>
        <NevsCard class="form">
            <h2>{{ $LANG.Get('labels.newMembershipNotifications') }}</h2>
            <NevsCheckbox v-model="client.send_new_memberships_notifications"
                          :label="$LANG.Get('fields.sendNewMembershipsNotifications')"></NevsCheckbox>
            <p class="disclaimer">{{ $LANG.Get('labels.newMembershipNotificationNote') }}</p>
        </NevsCard>
        <NevsCard class="form">
            <h2>{{ $LANG.Get('labels.documentSending') }}</h2>
            <p class="disclaimer">{{ $LANG.Get('labels.documentSendingNote') }}</p>
            <NevsTextField v-model="client.email_copy" :label="$LANG.Get('fields.emailCopy') + ':'"></NevsTextField>
        </NevsCard>
    </div>
</template>

<script>
import Client from "@/models/Client.js";
import NevsButton from "@/components/nevs/NevsButton.vue";
import NevsCard from "@/components/nevs/NevsCard.vue";
import NevsCheckbox from "@/components/nevs/NevsCheckbox.vue";
import NevsNumberField from "@/components/nevs/NevsNumberField.vue";
import NevsTextField from "@/components/nevs/NevsTextField.vue";

export default {
    name: "GeneralData",
    components: {NevsCheckbox, NevsNumberField, NevsTextField, NevsCard, NevsButton},
    data() {
        return {
            loaded: false,
            client: new Client(),
            clientValidation: new Client({}, true)
        }
    },
    watch: {
        // A reminder offers one way of paying, not two: picking either the renewal offer or the card payment
        // link clears the other.
        'client.membership_expiry_reminder_send_offer'(value) {
            if (value) this.client.membership_expiry_reminder_send_stripe_link = false;
        },
        'client.membership_expiry_reminder_send_stripe_link'(value) {
            if (value) this.client.membership_expiry_reminder_send_offer = false;
        }
    },
    methods: {
        saveClick() {
            let valid = true;

            // Without a positive lead time the reminder would go out on (or after) the day of expiry. The
            // reminder and new-membership texts themselves are fixed wording sent by the API, so there is
            // nothing else to validate here.
            if (this.client.send_membership_expiry_reminders) {
                if (!this.client.membership_expiry_reminder_days || parseInt(this.client.membership_expiry_reminder_days) <= 0) {
                    this.$LOCAL_BUS.TriggerEvent('popup', {text: this.$LANG.Get('fields.membershipExpiryReminderDays') + ': ' + this.$LANG.Get('alerts.fieldMissing'), type: 'alert'});
                    valid = false;
                }
            }

            if (valid) {
                // The column is an int, so a cleared field (only reachable with reminders off) falls back to the
                // default rather than sending a NaN the API would reject.
                let reminderDays = parseInt(this.client.membership_expiry_reminder_days);
                this.client.membership_expiry_reminder_days = isNaN(reminderDays) ? 7 : reminderDays;
                let vm = this;
                this.$API.APICall('put', 'client', this.client, (data, success) => {
                    if (success) {
                        vm.$LOCAL_BUS.TriggerEvent('notification', {text: vm.$LANG.Get('alerts.recordSaved')});
                        vm.$API.APICall('get', 'session', {}, (data, success) => {
                            if (success) {
                                vm.$store.commit('setUser', data.user);
                            }
                        }, false);
                    } else {
                        vm.$HELPERS.HandleServerError(vm, data);
                    }
                })
            }
        }
    },
    mounted() {

        if (!this.$store.state.user.permissions.includes('ADMINISTRATOR')) {
            this.$LOCAL_BUS.TriggerEvent('popup', {text: this.$LANG.Get('alerts.unauthorized'), type: 'alert'});
            this.$router.push('/');
        }
        this.$store.commit('selectMenu', 'settings');
        this.$store.commit('selectSubMenu', 'emailSettings');
        this.$store.commit('setBreadcrumbs', [
            {
                label: this.$LANG.Get('modules.settings'),
                link: null
            },
            {
                label: this.$LANG.Get('modules.emailSettings'),
                link: null
            }
        ]);

        let vm = this;
        this.$API.APICall('get', 'client', {}, (data, success) => {
            if (success) {
                if (data.client !== null) {
                    vm.client.Fill(data.client);
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            } else {
                vm.$HELPERS.HandleServerError(vm, data);
            }
            vm.$nextTick(() => {
                vm.loaded = true;
            });
        });
    }
}
</script>

<style scoped>
.form {
    max-width: 600px;
    margin-bottom: 10px;
}

h3 {
    margin-top: 15px;
    margin-bottom: 5px;
}

.disclaimer {
    font-size: 0.9em;
    opacity: 0.7;
    margin-bottom: 5px;
}
</style>